/**
 * Containerised Synkro install path.
 *
 * Replaces the host-side pueue + tmux + cc_sessions stack with a single
 * Docker container that bundles MCP server, pglite-db, grader dispatcher,
 * and N tmux-managed claude grader workers.
 *
 * Activated when SYNKRO_DEPLOYMENT_MODE=docker is set. The bare-host path
 * remains the default until two stable release cycles have validated this
 * path under real load (see Phase 5 in the architecture plan).
 *
 * Host-side files this module touches:
 *   ~/.synkro/                         (bind-mounted RO as /data/synkro-host;
 *                                       the container reads .mcp-jwt and
 *                                       credentials.json from inside it)
 *   ~/.synkro/pgdata/                  (bind-mounted RW, persistent telemetry)
 *   ~/.synkro/claude-creds/            (bind-mounted RW on macOS; symlinked
 *                                       from ~/.claude on Linux)
 *
 * Never bakes credentials into the image — every secret is a runtime bind
 * mount of a host file. See macKeychain.ts for the macOS bridge.
 */

import { copyFileSync, existsSync, mkdirSync, readFileSync, readdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { execSync, spawnSync } from 'node:child_process';
import { detectAgents } from '../installer/agentDetect.js';
import {
  CLAUDE_CREDS_DIR,
  CURSOR_CREDS_DIR,
  exportKeychainCreds,
  cursorApiKeyConfigured,
  needsKeychainBridge,
  writeRefreshAgent,
  loadRefreshAgent,
} from './macKeychain.js';

export const SYNKRO_DIR = join(homedir(), '.synkro');
const MCP_JWT_PATH = join(SYNKRO_DIR, '.mcp-jwt');
const PGDATA_PATH = join(SYNKRO_DIR, 'pgdata');
// Docker Desktop on macOS can't reliably bind-mount individual files (the mount
// shows up as -????? inside the container). Stage the host's ~/.claude.json
// inside a sibling directory and bind-mount the directory instead.
const CLAUDE_HOST_STATE_DIR = join(SYNKRO_DIR, 'claude-host-state');
const CLAUDE_HOST_STATE_FILE = join(CLAUDE_HOST_STATE_DIR, '.claude.json');

// Host-side ports the installer maps from the container's internal ports.
// Bumped by 10000 to avoid colliding with the bare-host install's defaults
// when both are sitting on the same machine during cutover (Phase 5).
const HOST_MCP_PORT = parseInt(process.env.SYNKRO_HOST_MCP_PORT || '18931', 10);
const HOST_GRADER_PORT = parseInt(process.env.SYNKRO_HOST_GRADER_PORT || '18929', 10);
const HOST_CWE_PORT = parseInt(process.env.SYNKRO_HOST_CWE_PORT || '18930', 10);
// PGLite TCP socket published from the container's internal 5433 so host tools
// (psql/DBeaver/TablePlus) can inspect the telemetry DB. The container's
// pglite-bootstrap multiplexer enforces access; the 127.0.0.1 bind is the
// network-layer allowlist.
const HOST_PGLITE_PORT = parseInt(process.env.SYNKRO_HOST_PGLITE_PORT || '15433', 10);

const CONTAINER_NAME = 'synkro-server';
// Canonical GHCR coordinates published by .github/workflows/docker-publish.yml.
// Override at install time via SYNKRO_IMAGE_TAG (or mirror to a private
// registry and set SYNKRO_IMAGE_REGISTRY for the hedge-fund deployment).
const DEFAULT_IMAGE = 'ghcr.io/synkro-sh/synkro-server:latest';

export class DockerInstallError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'DockerInstallError';
  }
}

export type WorkerProvider = 'claude_code' | 'cursor';

/**
 * Split a total worker count across the selected providers.
 *   - both providers → even split (an odd worker goes to Claude)
 *   - single provider → the whole pool
 *   - no providers   → treated as Claude-only (legacy default)
 * Used by `synkro install` and `synkro local-cc start|restart` so the split
 * rule lives in exactly one place.
 */
export function splitWorkers(total: number, providers: WorkerProvider[]): {
  claudeWorkers: number;
  cursorWorkers: number;
} {
  const t = Math.max(0, Math.floor(total));
  const hasClaude = providers.includes('claude_code');
  const hasCursor = providers.includes('cursor');
  if (hasClaude && hasCursor) {
    const cursorWorkers = Math.floor(t / 2);
    return { claudeWorkers: t - cursorWorkers, cursorWorkers };
  }
  if (hasCursor) return { claudeWorkers: 0, cursorWorkers: t };
  return { claudeWorkers: t, cursorWorkers: 0 };
}

/** Normalize a provider token. Accepts claude / claude-code / cc / cursor. */
export function normalizeProvider(p: string): WorkerProvider | null {
  const v = p.trim().toLowerCase();
  if (v === 'claude' || v === 'claude-code' || v === 'claude_code' || v === 'cc') return 'claude_code';
  if (v === 'cursor') return 'cursor';
  return null;
}

export type PoolResolution = {
  /** Explicit per-kind counts, when the file specifies any. */
  claudeWorkers?: number;
  cursorWorkers?: number;
  /** Shorthand mode when no explicit counts are given. */
  pool: 'auto' | 'claude_code' | 'cursor';
  /** Human-readable problems (unrecognized tokens) — callers should surface these. */
  warnings: string[];
};

/**
 * Resolve the grader pool from a parsed `.synkro` object into a single shape
 * that every caller (`install`, `local-cc start/restart`) agrees on. This is
 * the ONE place the pool/worker config schema is interpreted.
 *
 * Sources, in priority order:
 *   1. Numeric provider counts directly under `grader:` (the unified form) —
 *        grader: { claude-code: 5, cursor: 3 }   // mixed pool, any count
 *      Aliases (claude / claude-code / cc / cursor) all normalize.
 *   2. Legacy top-level `workers:` block — workers: { claude: 5, cursor: 3 }
 *   3. `grader.pool` string shorthand: auto | claude-code | cursor (+ aliases)
 *
 * Unrecognized tokens produce a warning instead of a silent fallback so a
 * typo'd provider name can't quietly halve the pool.
 */
export function resolveGraderPool(parsed: Record<string, any>): PoolResolution {
  const warnings: string[] = [];
  const grader = parsed?.grader && typeof parsed.grader === 'object' ? parsed.grader : {};

  // 1+2. Collect explicit numeric counts from grader (unified) then the legacy
  // workers block. Unknown numeric keys under grader warn (likely a typo).
  const counts: { claude_code?: number; cursor?: number } = {};
  const collect = (obj: any, where: string, flagUnknown: boolean) => {
    if (!obj || typeof obj !== 'object') return;
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v !== 'number') continue;
      const np = normalizeProvider(k);
      if (np === 'claude_code') counts.claude_code = Math.max(0, Math.floor(v));
      else if (np === 'cursor') counts.cursor = Math.max(0, Math.floor(v));
      else if (flagUnknown) {
        warnings.push(`synkro.toml: ${where}.${k} is not a known provider (use claude-code or cursor) — ignored`);
      }
    }
  };
  collect(grader, 'grader', true);
  collect(parsed?.workers, 'workers', false);

  // 3. pool string shorthand.
  let pool: 'auto' | 'claude_code' | 'cursor' = 'auto';
  const rawPool = grader.pool;
  if (rawPool != null && rawPool !== 'auto') {
    const np = normalizeProvider(String(rawPool));
    if (np) pool = np;
    else warnings.push(`synkro.toml: grader.pool="${rawPool}" is not recognized (use auto | claude-code | cursor) — falling back to auto`);
  }

  if (counts.claude_code != null || counts.cursor != null) {
    return { claudeWorkers: counts.claude_code, cursorWorkers: counts.cursor, pool, warnings };
  }
  return { pool, warnings };
}

/** Friendly label for display/logging from the internal pool form. */
export function poolLabel(pool: 'auto' | 'claude_code' | 'cursor'): string {
  return pool === 'claude_code' ? 'claude-code' : pool;
}

// Coerce a single TOML value token to JS.
function parseTomlValue(raw: string): any {
  const v = raw.trim();
  if (v.startsWith('[') && v.endsWith(']')) {
    const inner = v.slice(1, -1).trim();
    if (!inner) return [];
    return inner.split(',').map(s => parseTomlValue(s)).filter(s => s !== '');
  }
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) return v.slice(1, -1);
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (/^-?\d+$/.test(v)) return parseInt(v, 10);
  return v;
}

// Minimal TOML reader for synkro.toml's fixed schema (top-level scalars/arrays
// + [grader]/[scanning] tables). Returns the same nested-object shape the old
// YAML parser did, so resolveGraderPool() is unchanged.
function parseSynkroToml(raw: string): Record<string, any> {
  const result: Record<string, any> = {};
  let section: string | null = null;
  for (const rawLine of raw.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;
    const sec = line.match(/^\[([^\]]+)\]$/);
    if (sec) {
      section = sec[1].trim();
      if (!result[section]) result[section] = {};
      continue;
    }
    const eq = line.indexOf('=');
    if (eq === -1) continue;
    const key = line.slice(0, eq).trim().replace(/^["']|["']$/g, '');
    let valRaw = line.slice(eq + 1).trim();
    if (!valRaw.startsWith('"') && !valRaw.startsWith("'") && !valRaw.startsWith('[')) {
      const h = valRaw.indexOf('#');
      if (h !== -1) valRaw = valRaw.slice(0, h).trim();
    }
    const value = parseTomlValue(valRaw);
    if (section) (result[section] as Record<string, any>)[key] = value;
    else result[key] = value;
  }
  return result;
}

function readSynkroFileConfig(): PoolResolution {
  try {
    const root = execSync('git rev-parse --show-toplevel 2>/dev/null', { encoding: 'utf-8', timeout: 3000, stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    if (!root) return { pool: 'auto', warnings: [] };
    const fp = join(root, 'synkro.toml');
    if (!existsSync(fp)) return { pool: 'auto', warnings: [] };
    const parsed = parseSynkroToml(readFileSync(fp, 'utf-8'));
    return resolveGraderPool(parsed);
  } catch {}
  return { pool: 'auto', warnings: [] };
}

/**
 * Parse `--workers N|--workers=N` and `--provider(s) a,b` flags from a
 * start/restart invocation and compute the per-kind worker split.
 *   - `explicit` is true when any worker/provider flag was supplied — callers
 *     use it to decide between a config-changing recreate vs a plain start.
 *   - Providers default to the coding agents detected on the machine.
 *   - Total defaults to 8, capped at 64.
 */
export function resolveWorkerConfig(rest: string[]): {
  claudeWorkers: number;
  cursorWorkers: number;
  explicit: boolean;
} {
  let workers = 8;
  let explicit = false;
  const providers: WorkerProvider[] = [];
  const addProviders = (csv: string) => {
    for (const p of csv.split(',')) {
      const np = normalizeProvider(p);
      if (np && !providers.includes(np)) providers.push(np);
    }
  };
  for (let i = 0; i < rest.length; i++) {
    const a = rest[i];
    if (a === '--workers' || a === '-w') { workers = parseInt(rest[++i] || '8', 10); explicit = true; }
    else if (a.startsWith('--workers=')) { workers = parseInt(a.slice('--workers='.length), 10); explicit = true; }
    else if (a === '--provider' || a === '--providers') { addProviders(rest[++i] || ''); explicit = true; }
    else if (a.startsWith('--provider=')) { addProviders(a.slice('--provider='.length)); explicit = true; }
    else if (a.startsWith('--providers=')) { addProviders(a.slice('--providers='.length)); explicit = true; }
  }
  if (!Number.isFinite(workers) || workers < 1) workers = 8;
  workers = Math.min(workers, 64);
  let provs = providers;
  if (provs.length === 0) {
    const sc = readSynkroFileConfig();
    for (const w of sc.warnings) console.warn(`  ⚠ ${w}`);
    if (sc.claudeWorkers != null || sc.cursorWorkers != null) {
      const cw = sc.claudeWorkers || 0;
      const curw = sc.cursorWorkers || 0;
      if (cw + curw > 0) return { claudeWorkers: cw, cursorWorkers: curw, explicit };
    }
    if (sc.pool === 'cursor') {
      provs = ['cursor'];
    } else if (sc.pool === 'claude_code') {
      provs = ['claude_code'];
    } else {
      provs = detectAgents().map(a => a.kind) as WorkerProvider[];
      if (provs.length === 0) provs = ['claude_code'];
    }
  }
  return { ...splitWorkers(workers, provs), explicit };
}

/** Resolve which image tag this install should pull. */
export function imageTag(): string {
  const registry = process.env.SYNKRO_IMAGE_REGISTRY || ''; // e.g. registry.hedgefund.internal
  const tag = process.env.SYNKRO_IMAGE_TAG || DEFAULT_IMAGE;
  return registry ? `${registry.replace(/\/+$/, '')}/${tag.replace(/^.*\//, '')}` : tag;
}

/** True iff docker is on PATH and the daemon is reachable. */
export function assertDockerAvailable(): void {
  const v = spawnSync('docker', ['version', '--format', '{{.Server.Version}}'], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  if (v.status !== 0) {
    throw new DockerInstallError(
      'Docker CLI is not installed or the daemon is not reachable.\n' +
      'Install Docker Desktop (macOS) or docker-engine (Linux), start the daemon, then re-run.',
    );
  }
}

/**
 * Compute the bind-mount source for the claude credentials dir. macOS uses
 * the exported keychain shadow; Linux uses ~/.claude directly.
 */
function claudeCredsHostDir(): string {
  if (needsKeychainBridge()) return CLAUDE_CREDS_DIR;
  return join(homedir(), '.claude');
}

/**
 * Idempotent docker pull + run. If a container with the same name is already
 * up, this stops it first so the new image starts cleanly (matches
 * `synkro update` semantics).
 *
 * Returns the resolved image tag for logging.
 */
/**
 * Resolve the absolute path to the `synkro` binary for the launchd refresh
 * agent. The location varies by install method (Homebrew's /opt/homebrew/bin,
 * /usr/local/bin, an npm prefix) — never hardcode it, or the refresh job
 * silently fails and the container's Claude creds expire.
 */
function resolveSynkroBin(): string {
  const which = spawnSync('which', ['synkro'], { encoding: 'utf-8', timeout: 5_000 });
  const resolved = (which.stdout || '').split('\n')[0].trim();
  return resolved || 'synkro';
}

// Host-side PGLite orphans from pre-Docker installs (or earlier crashed
// container starts that re-spawned the host bin) squat on the published
// PGLite port, hold the only client slot under the old maxConnections=1
// default, and write into a separate pgdata from the container's mount.
// The migration commit that moved PGLite into the container deleted the
// source file but never killed running processes — they daemonize to
// launchd (PPID=1) and outlive the install that spawned them. Sweep them
// before launching the new container so the publish maps to the right
// PGLite. Container bun processes are isolated inside their PID namespace
// and never appear in the host's ps output, so this is safe.
function sweepHostPglite(): void {
  const ps = spawnSync('ps', ['-eo', 'pid,command'], { encoding: 'utf-8', timeout: 5_000 });
  if (ps.status !== 0) return;
  const targets: number[] = [];
  for (const line of (ps.stdout || '').split('\n')) {
    if (!/bun\b/.test(line)) continue;
    if (!/pglite-(db|bootstrap)\.ts\b/.test(line)) continue;
    const m = line.trim().match(/^(\d+)\s/);
    if (!m) continue;
    const pid = parseInt(m[1], 10);
    if (pid > 0 && pid !== process.pid) targets.push(pid);
  }
  if (targets.length === 0) return;
  console.log(`  Sweeping ${targets.length} stale host PGLite process(es): ${targets.join(', ')}`);
  for (const pid of targets) {
    try { process.kill(pid, 'SIGTERM'); } catch {}
  }
  const deadline = Date.now() + 3000;
  while (Date.now() < deadline) {
    const alive = targets.filter(pid => {
      try { process.kill(pid, 0); return true; } catch { return false; }
    });
    if (alive.length === 0) return;
    spawnSync('sleep', ['0.2'], { timeout: 1_000 });
  }
  for (const pid of targets) {
    try { process.kill(pid, 'SIGKILL'); } catch {}
  }
}

export async function dockerInstall(opts: {
  workersPerPool?: number;
  claudeWorkers?: number;
  cursorWorkers?: number;
  connectedRepo?: string;
} = {}): Promise<{
  image: string;
  hostMcpPort: number;
  hostGraderPort: number;
  hostCwePort: number;
  hostPglitePort: number;
}> {
  assertDockerAvailable();

  const image = imageTag();
  // Per-kind worker counts. claudeWorkers falls back to the legacy
  // workersPerPool; cursorWorkers defaults to 0 (Claude-only install).
  const claudeWorkers = opts.claudeWorkers ?? opts.workersPerPool ?? 8;
  const cursorWorkers = opts.cursorWorkers ?? 0;
  const totalWorkers = claudeWorkers + cursorWorkers;

  // Ensure persistent host dirs exist before docker tries to bind-mount them.
  // Docker creates missing bind sources as root-owned dirs which then break
  // permissions inside the container — pre-create them as the host user.
  mkdirSync(PGDATA_PATH, { recursive: true });
  mkdirSync(BACKUP_DIR, { recursive: true });

  // Stage the host's ~/.claude.json into a dir we can bind-mount cleanly on
  // macOS. Without this the container's workers see -????? at the mount path
  // and Claude Code prompts for browser OAuth on every launch.
  mkdirSync(CLAUDE_HOST_STATE_DIR, { recursive: true });
  const hostClaudeJson = join(homedir(), '.claude.json');
  if (existsSync(hostClaudeJson)) {
    copyFileSync(hostClaudeJson, CLAUDE_HOST_STATE_FILE);
  }
  if (!existsSync(MCP_JWT_PATH)) {
    throw new DockerInstallError(
      `MCP JWT missing at ${MCP_JWT_PATH}. The installer should mint this before calling dockerInstall.`,
    );
  }

  // Pre-create the Cursor creds dir so the bind mount target is owned by the
  // host user (Docker would otherwise create it root-owned).
  mkdirSync(CURSOR_CREDS_DIR, { recursive: true });

  // On macOS, export keychain creds + install the launchd refresh agent
  // before starting the container so the workers have something to auth with.
  if (needsKeychainBridge()) {
    // Export the Claude Code keychain creds so the container holds a subscription
    // token. Two consumers need it: Claude grader workers AND the out-of-band usage
    // tap (which pings Anthropic to read account-wide utilization). The tap runs even
    // when grading is pure BYOK or pure Cursor, so we export whenever the keychain
    // entry exists — NOT only when there are Claude workers. Hard-fail only when
    // Claude grading actually requires it; otherwise degrade to "telemetry off".
    const claudeCredsPath = exportKeychainCreds();
    if (!claudeCredsPath) {
      if (claudeWorkers > 0) {
        throw new DockerInstallError(
          'Claude Code keychain entry not found. Run `claude login` (or open Claude Code and sign in) before installing the container.',
        );
      }
      console.warn('  ⚠ Claude Code keychain entry not found — live usage telemetry stays off until you sign in to Claude Code (grading is unaffected).');
    }
    // Cursor uses a dashboard API key (the @cursor/sdk rejects the keychain
    // OAuth token). Warn — don't hard-fail — so a "both agents" install still
    // proceeds; the Cursor pool stays idle until the key file is present.
    if (cursorWorkers > 0 && !cursorApiKeyConfigured()) {
      console.warn('  ⚠ No Cursor API key found — Cursor grader workers will be idle.');
      console.warn('    Generate a key at cursor.com → Settings → API Keys, then:');
      console.warn(`      echo 'YOUR_KEY' > ~/.synkro/cursor-creds/api-key && chmod 600 ~/.synkro/cursor-creds/api-key`);
    }
    // launchd refresh agent — Claude only (Cursor's API key is static).
    const plist = writeRefreshAgent(resolveSynkroBin());
    try { loadRefreshAgent(); } catch (err) {
      console.warn(`  ⚠ launchd refresh agent not loaded: ${(err as Error).message}`);
      console.warn(`    Plist written to ${plist} — load manually with launchctl bootstrap when ready.`);
    }
  } else {
    // Linux — make sure ~/.claude exists so the bind mount target is real.
    mkdirSync(join(homedir(), '.claude'), { recursive: true });
  }

  // Pull (or update) the image — unless we're using a locally-built image.
  // A local/air-gapped image won't exist in any registry, so a forced pull
  // would fail. Skip the pull when SYNKRO_SKIP_PULL is set, and fall back to an
  // existing local image if the pull fails for any reason (offline, private
  // registry blip, locally-built dev image). Only hard-fail when there is no
  // local image to fall back to.
  const imageExistsLocally = (): boolean =>
    spawnSync('docker', ['image', 'inspect', image], { stdio: 'ignore', timeout: 30_000 }).status === 0;
  const skipPull = process.env.SYNKRO_SKIP_PULL === '1' || process.env.SYNKRO_SKIP_PULL === 'true';

  if (skipPull && imageExistsLocally()) {
    console.log(`  Using local image ${image} (SYNKRO_SKIP_PULL set — pull skipped).`);
  } else {
    console.log(`  Pulling ${image}...`);
    const pull = spawnSync('docker', ['pull', image], { encoding: 'utf-8', stdio: 'inherit', timeout: 600_000 });
    if (pull.status !== 0) {
      if (imageExistsLocally()) {
        console.warn(`  ⚠ docker pull ${image} failed — falling back to the existing local image.`);
      } else {
        throw new DockerInstallError(`docker pull ${image} failed`);
      }
    }
  }

  // Gracefully stop existing container if running (snapshot + checkpoint).
  const existing = dockerStatus();
  if (existing.running) {
    console.log('  Stopping existing container gracefully...');
    await dockerSafeStop();
  }
  spawnSync('docker', ['rm', CONTAINER_NAME], { encoding: 'utf-8', timeout: 30_000 });

  // Kill any leftover host-side PGLite from pre-Docker installs so the new
  // container's published port 5433 isn't shadowed by a stale daemon.
  sweepHostPglite();

  // Start the container with bind mounts and explicit port maps (per the
  // approved plan — explicit mapping, not --network host).
  //
  // SECURITY: every -p below MUST keep the `127.0.0.1:` host-bind prefix. The
  // container's /api/local/* REST endpoints (snapshot, telemetry, hook-config)
  // are unauthenticated — the localhost-only bind IS the access boundary.
  // Never drop the prefix or switch to 0.0.0.0 / --network host: that would
  // expose an unauthenticated control surface to the LAN.
  const credsDir = claudeCredsHostDir();
  const args = [
    'run',
    '--detach',
    '--name', CONTAINER_NAME,
    '--restart', 'unless-stopped',
    '-p', `127.0.0.1:${HOST_MCP_PORT}:8931`,
    '-p', `127.0.0.1:${HOST_GRADER_PORT}:8929`,
    '-p', `127.0.0.1:${HOST_CWE_PORT}:8930`,
    '-p', `127.0.0.1:${HOST_PGLITE_PORT}:5433`,
    '-v', `${PGDATA_PATH}:/data/pgdata`,
    '-v', `${BACKUP_DIR}:/data/backups`,
    // The whole host ~/.synkro directory, read-only. The container copies
    // .mcp-jwt and credentials.json out of it at boot. A directory mount
    // sidesteps Docker Desktop for macOS's unreliable single-file bind mounts
    // (which previously left a dangling symlink that blocked container start).
    '-v', `${SYNKRO_DIR}:/data/synkro-host:ro`,
    '-v', `${credsDir}:/home/synkro/.claude:rw`,
    '-v', `${join(homedir(), '.claude')}:/data/claude-host:ro`,
    '-v', `${CLAUDE_HOST_STATE_DIR}:/data/claude-host-state:ro`,
    // Cursor creds — mounted RW so the in-container refresher can rotate the
    // access token in place. Only mounted when the install includes Cursor.
    ...(cursorWorkers > 0
        ? ['-v', `${CURSOR_CREDS_DIR}:/home/synkro/.cursor-creds:rw`]
        : []),
    '-e', `WORKERS_PER_POOL=${totalWorkers}`,
    '-e', `CLAUDE_WORKERS=${claudeWorkers}`,
    '-e', `CURSOR_WORKERS=${cursorWorkers}`,
    // Pass through the batch-size lever if the operator set it. Defaults
    // inside the container to 5; clamped to [1, 20] by synkro-server.ts.
    ...(process.env.SYNKRO_MAX_BATCH_SIZE
        ? ['-e', `SYNKRO_MAX_BATCH_SIZE=${process.env.SYNKRO_MAX_BATCH_SIZE}`]
        : []),
    // Cursor grading model — tunable like SYNKRO_MAX_BATCH_SIZE.
    ...(process.env.SYNKRO_CURSOR_MODEL
        ? ['-e', `SYNKRO_CURSOR_MODEL=${process.env.SYNKRO_CURSOR_MODEL}`]
        : []),
    // Fix-poll kill switch. Default ON in the image; a benchmark/headless run
    // (e.g. sec-code-bench) sets SYNKRO_FIX_POLL=0 so ask-mode violations skip the
    // interactive AskUserQuestion poll and fall through to generate-the-fix. Only
    // passed when the operator set it; otherwise the image default (1) applies.
    ...(process.env.SYNKRO_FIX_POLL
        ? ['-e', `SYNKRO_FIX_POLL=${process.env.SYNKRO_FIX_POLL}`]
        : []),
    // Connected repo — the server seeds the local app + ruleset named after it.
    ...(opts.connectedRepo
        ? ['-e', `SYNKRO_CONNECTED_REPO=${opts.connectedRepo}`]
        : []),
    // Real account identity (from config.env via process.env) — telemetry is
    // stamped with these instead of a `local-user` placeholder, so the data is
    // correctly attributed from the first graded command.
    ...(process.env.SYNKRO_ORG_ID
        ? ['-e', `SYNKRO_ORG_ID=${process.env.SYNKRO_ORG_ID}`]
        : []),
    ...(process.env.SYNKRO_USER_ID
        ? ['-e', `SYNKRO_USER_ID=${process.env.SYNKRO_USER_ID}`]
        : []),
    ...(process.env.SYNKRO_EMAIL
        ? ['-e', `SYNKRO_EMAIL=${process.env.SYNKRO_EMAIL}`]
        : []),
    image,
  ];
  const run = spawnSync('docker', args, { encoding: 'utf-8', stdio: 'inherit', timeout: 60_000 });
  if (run.status !== 0) {
    throw new DockerInstallError(`docker run failed (image ${image})`);
  }

  return { image, hostMcpPort: HOST_MCP_PORT, hostGraderPort: HOST_GRADER_PORT, hostCwePort: HOST_CWE_PORT, hostPglitePort: HOST_PGLITE_PORT };
}

/**
 * Poll the container's MCP /healthz until it returns 200, or fail after the
 * timeout. Used by install to give a single clean ready signal instead of the
 * multi-port wait the bare-host install has to do.
 */
export async function waitForContainerReady(timeoutMs = 60_000): Promise<boolean> {
  const start = Date.now();
  const url = `http://127.0.0.1:${HOST_MCP_PORT}/health`;
  while (Date.now() - start < timeoutMs) {
    try {
      const r = await fetch(url, { signal: AbortSignal.timeout(2_000) });
      if (r.ok) return true;
    } catch { /* keep retrying */ }
    await new Promise(r => setTimeout(r, 1_000));
  }
  return false;
}

export async function waitForWorkersReady(timeoutMs = 60_000): Promise<boolean> {
  const start = Date.now();
  const url = `http://127.0.0.1:${HOST_GRADER_PORT}/healthz`;
  while (Date.now() - start < timeoutMs) {
    try {
      const r = await fetch(url, { signal: AbortSignal.timeout(2_000) });
      if (r.ok) {
        const data = await r.json() as { workers?: number; healthy?: number };
        if ((data.healthy || 0) > 0) return true;
      }
    } catch {}
    await new Promise(r => setTimeout(r, 1_000));
  }
  return false;
}

/**
 * Remove the container (after it's already stopped). Does NOT stop gracefully —
 * callers must use dockerSafeStop() first if the container is running.
 */
export function dockerRemove(): void {
  spawnSync('docker', ['rm', CONTAINER_NAME], { encoding: 'utf-8', timeout: 30_000 });
}

/** @deprecated Use dockerSafeStop() + dockerRemove() instead. */
export function dockerStop(): void {
  spawnSync('docker', ['stop', '--timeout=30', CONTAINER_NAME], { encoding: 'utf-8', timeout: 45_000 });
  spawnSync('docker', ['rm', CONTAINER_NAME], { encoding: 'utf-8', timeout: 30_000 });
}

/**
 * `synkro update` for the containerised install — pull + restart, preserve
 * all bind-mounted state.
 */
export async function dockerUpdate(opts: {
  workersPerPool?: number;
  claudeWorkers?: number;
  cursorWorkers?: number;
  connectedRepo?: string;
} = {}): Promise<void> {
  if (dockerStatus().running) {
    await dockerSafeStop();
  }
  dockerRemove();
  await dockerInstall(opts);
}

/** Show host-mapped ports + container status. */
export function dockerStatus(): { running: boolean; image?: string; healthz?: string } {
  const r = spawnSync('docker', ['inspect', '--format', '{{.State.Status}}', CONTAINER_NAME], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  const status = (r.stdout || '').trim();
  if (status !== 'running') return { running: false };
  return {
    running: true,
    image: imageTag(),
    healthz: `http://127.0.0.1:${HOST_MCP_PORT}/`,
  };
}

/**
 * Read the worker-pool config baked into the installed container's env, so
 * `synkro update` can rebuild on a fresh image without resetting the pool.
 * Works on stopped containers too. Returns null when no container exists.
 */
export function readContainerConfig(): {
  claudeWorkers?: number;
  cursorWorkers?: number;
  connectedRepo?: string;
} | null {
  const r = spawnSync('docker', ['inspect', '--format', '{{json .Config.Env}}', CONTAINER_NAME], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  if (r.status !== 0 || !r.stdout) return null;
  let env: unknown;
  try { env = JSON.parse(r.stdout.trim()); } catch { return null; }
  if (!Array.isArray(env)) return null;
  const get = (k: string): string | undefined => {
    const hit = env.find((e): e is string => typeof e === 'string' && e.startsWith(k + '='));
    return hit ? hit.slice(k.length + 1) : undefined;
  };
  const num = (s: string | undefined): number | undefined => {
    if (s === undefined) return undefined;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    claudeWorkers: num(get('CLAUDE_WORKERS')),
    cursorWorkers: num(get('CURSOR_WORKERS')),
    connectedRepo: get('SYNKRO_CONNECTED_REPO') || undefined,
  };
}

// ─── Safe lifecycle commands ────────────────────────────────────────────────

const BACKUP_DIR = join(SYNKRO_DIR, 'pgdata-backups');

/**
 * Graceful stop: snapshot → docker stop (30s for CHECKPOINT+flush) → verify.
 * Never force-kills. Returns details about what happened.
 */
export async function dockerSafeStop(): Promise<{
  ok: boolean;
  snapshot?: { ok: boolean; error?: string };
  exitCode?: number;
  pgdataCheck?: { healthy: boolean; details: string };
}> {
  const status = dockerStatus();
  if (!status.running) {
    console.log('  Container is not running.');
    return { ok: true, pgdataCheck: checkPgdata() };
  }

  // 1. Request a pre-shutdown snapshot via the REST endpoint
  console.log('  Requesting data snapshot before shutdown...');
  let snapshot: { ok: boolean; error?: string } = { ok: false, error: 'not attempted' };
  try {
    const resp = await fetch(`http://127.0.0.1:${HOST_MCP_PORT}/api/local/snapshot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: 'cli-stop' }),
      signal: AbortSignal.timeout(20_000),
    });
    if (resp.ok) {
      snapshot = { ok: true };
      console.log('  ✓ Snapshot saved.');
    } else {
      snapshot = { ok: false, error: `HTTP ${resp.status}` };
      console.warn(`  ⚠ Snapshot request failed (HTTP ${resp.status}). Proceeding with stop.`);
    }
  } catch (e) {
    snapshot = { ok: false, error: String(e).slice(0, 100) };
    console.warn(`  ⚠ Snapshot request failed: ${snapshot.error}. Proceeding with stop.`);
  }

  // 2. docker stop with 30s grace period (entrypoint trap does CHECKPOINT + final snapshot)
  console.log('  Stopping container (30s grace for CHECKPOINT + WAL flush)...');
  const stop = spawnSync('docker', ['stop', '--timeout=30', CONTAINER_NAME], {
    encoding: 'utf-8',
    timeout: 45_000,
  });

  // 3. Verify clean exit
  const inspect = spawnSync('docker', ['inspect', '--format', '{{.State.ExitCode}}', CONTAINER_NAME], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  const exitCode = parseInt((inspect.stdout || '').trim(), 10);
  if (exitCode === 0) {
    console.log('  ✓ Container stopped cleanly (exit 0).');
  } else {
    console.warn(`  ⚠ Container exited with code ${exitCode}.`);
  }

  // 4. Check pgdata health on host
  const pgCheck = checkPgdata();
  if (pgCheck.healthy) {
    console.log(`  ✓ pgdata looks healthy: ${pgCheck.details}`);
  } else {
    console.warn(`  ⚠ pgdata check: ${pgCheck.details}`);
  }

  return { ok: stop.status === 0, snapshot, exitCode, pgdataCheck: pgCheck };
}

/**
 * Start the container (must already exist from a prior install).
 * Checks pgdata integrity, starts container, waits for healthcheck.
 */
export async function dockerSafeStart(): Promise<{
  ok: boolean;
  pgdataState: string;
  error?: string;
}> {
  // Check if already running
  const status = dockerStatus();
  if (status.running) {
    console.log('  Container is already running.');
    return { ok: true, pgdataState: 'running' };
  }

  // Check if container exists (stopped)
  const exists = spawnSync('docker', ['inspect', '--format', '{{.State.Status}}', CONTAINER_NAME], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  if (exists.status !== 0) {
    return { ok: false, pgdataState: 'no_container', error: 'No synkro-server container found. Run `synkro install` first.' };
  }

  // Check pgdata state
  const pgCheck = checkPgdata();
  if (existsSync(PGDATA_PATH) && readdirSync(PGDATA_PATH).length > 0) {
    if (pgCheck.healthy) {
      console.log(`  pgdata: existing data found — ${pgCheck.details}`);
    } else {
      console.warn(`  ⚠ pgdata: ${pgCheck.details}`);
      console.log('  Starting anyway — entrypoint will attempt recovery from snapshots if needed.');
    }
  } else {
    console.log('  pgdata: no existing data — fresh start.');
    mkdirSync(PGDATA_PATH, { recursive: true });
  }

  // Start the container
  console.log('  Starting container...');
  const start = spawnSync('docker', ['start', CONTAINER_NAME], {
    encoding: 'utf-8',
    timeout: 30_000,
  });
  if (start.status !== 0) {
    return { ok: false, pgdataState: 'start_failed', error: `docker start failed: ${(start.stderr || '').slice(0, 200)}` };
  }

  // Wait for healthcheck
  console.log('  Waiting for server to become healthy...');
  const ready = await waitForContainerReady(60_000);
  if (ready) {
    console.log('  ✓ Server is healthy and ready.');
    return { ok: true, pgdataState: pgCheck.healthy ? 'existing' : 'recovered' };
  } else {
    return { ok: false, pgdataState: 'unhealthy', error: 'Server did not become healthy within 60s. Check: docker logs synkro-server' };
  }
}

/**
 * Restart: full safe stop, then safe start. Sequential — never overlapping.
 */
export async function dockerSafeRestart(): Promise<{ ok: boolean; stop: Awaited<ReturnType<typeof dockerSafeStop>>; start: Awaited<ReturnType<typeof dockerSafeStart>> }> {
  console.log('  === Stop ===');
  const stopResult = await dockerSafeStop();
  if (!stopResult.ok) {
    console.error('  Stop failed. Aborting restart.');
    return { ok: false, stop: stopResult, start: { ok: false, pgdataState: 'not_started', error: 'stop failed' } };
  }
  console.log('\n  === Start ===');
  const startResult = await dockerSafeStart();
  return { ok: startResult.ok, stop: stopResult, start: startResult };
}

function checkPgdata(): { healthy: boolean; details: string } {
  if (!existsSync(PGDATA_PATH)) return { healthy: false, details: 'pgdata directory does not exist' };
  const entries = readdirSync(PGDATA_PATH);
  if (entries.length === 0) return { healthy: true, details: 'empty (fresh start)' };
  const hasPidFile = entries.includes('postmaster.pid');
  const hasWalDir = entries.includes('pg_wal');
  const hasPgControl = entries.includes('global') || entries.includes('pg_control');
  if (hasPidFile) return { healthy: false, details: 'stale postmaster.pid present (unclean shutdown)' };
  if (!hasWalDir) return { healthy: false, details: 'pg_wal directory missing' };
  if (!hasPgControl) return { healthy: false, details: 'pg_control/global directory missing' };
  return { healthy: true, details: `${entries.length} entries, WAL present, no stale PID` };
}
